import { useActor } from "@xstate/react";
import parsePhoneNumberFromString from "libphonenumber-js";
import { useCallback, useEffect, useMemo, useRef } from "react";

import useZodValidation from "@/hooks/useZodValidation";
import { defaultNS, useTranslation } from "@/lib/i18n/";

import { type PhoneValue, createPhoneInputMachine } from "./state.machine";

/**
 * 電話番号入力フック
 *
 * - マシンからのイベント発行を購読
 * - 内部状態とUIイベントを分離
 * - 副作用を最小限に抑制
 */
export const useInputPhone = (
  value: PhoneValue,
  onChange: (value: PhoneValue) => void,
  required: boolean,
) => {
  const { t } = useTranslation(defaultNS);
  useZodValidation();

  // 初回レンダリング値の参照を保持
  const initialValueRef = useRef(value);

  // マシンの生成はメモ化
  const machine = useMemo(
    () =>
      createPhoneInputMachine({
        initialValue: initialValueRef.current,
        required,
        t,
      }),
    [required, t],
  );

  // アクターを使用してマシンを実行
  const [state, send, actor] = useActor(machine);

  // マシンからの値変更イベントを購読
  useEffect(() => {
    const subscription = actor.on("valueChange", (event) => {
      // このタイミングで先頭の0を付与した値に変更
      const phoneNumber =
        parsePhoneNumberFromString(
          `+${event.value.dialCode} ${event.value.phoneNumber.replace(/[- ]/g, "")}`,
        )
          ?.formatNational()
          ?.replace(/[- ]/g, "") ?? event.value.phoneNumber; // 日本の場合、ハイフンが間に入る。イタリア等の場合、半角スペースが間に入る。

      onChange({
        phoneNumber: phoneNumber,
        dialCode: event.value.dialCode,
      });
    });

    return () => subscription.unsubscribe();
  }, [actor, onChange]);

  const {
    formattedNumber,
    selectedDialCode,
    error,
    isOpen,
    searchText,
    countryOptions,
  } = state.context;

  const handlePhoneNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      send({ type: "PHONE_NUMBER_CHANGE", value: e.target.value });
    },
    [send],
  );

  const handleDialCodeChange = useCallback(
    (dialCode: string | null) => {
      if (dialCode) {
        send({ type: "DIAL_CODE_SELECT", dialCode });
      }
    },
    [send],
  );

  const handleSearchChange = useCallback(
    (query: string) => {
      send({ type: "COUNTRY_SEARCH", query });
    },
    [send],
  );

  const togglePopover = useCallback(() => {
    send({ type: "TOGGLE_POPOVER" });
  }, [send]);

  return {
    formattedNumber,
    selectedDialCode,
    error,
    isOpen,
    searchText,
    countryOptions,
    handlePhoneNumberChange,
    handleDialCodeChange,
    handleSearchChange,
    togglePopover,
  };
};
